import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, Easing } from "remotion";
import { Backdrop, Title, Kicker, useReveal } from "../components";
import { COLORS, FONT, NEUTRAL, SAFE } from "../theme";

const CameraIcon = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={COLORS.blueLight} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
    <circle cx="12" cy="13" r="3.2" />
  </svg>
);
const SignIcon = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={COLORS.greenLight} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 18c3 0 3-6 6-6s2 4 4 4 3-9 5-9" />
    <path d="M3 21h18" />
  </svg>
);
const CheckIcon = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={COLORS.purpleLight} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 4h9a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3Z" />
    <path d="M9 4a1.5 1.5 0 0 1 3 0h-3Z" fill={COLORS.purpleLight} />
    <path d="M8.5 11.5l2 2 4-4" />
  </svg>
);

const Item: React.FC<{
  icon: React.ReactNode;
  accent: string;
  title: string;
  desc: string;
  start: number;
}> = ({ icon, accent, title, desc, start }) => {
  const r = useReveal(start);
  return (
    <div
      style={{
        display: "flex",
        gap: 26,
        alignItems: "center",
        padding: "26px 30px",
        borderRadius: 22,
        background: NEUTRAL.glassBg,
        border: `1px solid ${NEUTRAL.line}`,
        width: 720,
        ...r,
      }}
    >
      <div
        style={{
          width: 96,
          height: 96,
          borderRadius: 20,
          background: `${accent}1e`,
          border: `1px solid ${accent}44`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontFamily: FONT, fontWeight: 800, fontSize: 40, color: COLORS.white, letterSpacing: -0.5 }}>
          {title}
        </div>
        <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 28, color: COLORS.sub, marginTop: 6 }}>
          {desc}
        </div>
      </div>
    </div>
  );
};

// Teléfono con "captura" de fotos animada.
const Phone: React.FC<{ delay: number }> = ({ delay }) => {
  const frame = useCurrentFrame();
  const f = frame - delay;
  const op = interpolate(f, [0, 24], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const y = interpolate(f, [0, 26], [50, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) });
  return (
    <div
      style={{
        width: 430,
        height: 860,
        borderRadius: 54,
        background: "#0C1626",
        border: `10px solid #1B2A3E`,
        boxShadow: "0 40px 100px rgba(0,0,0,0.5)",
        padding: 26,
        opacity: op,
        translate: `0px ${y}px`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: 26, color: COLORS.sub, marginBottom: 18 }}>
        Evidencia · Etapa PDI
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {[0, 1, 2, 3].map((i) => {
          const fillOp = interpolate(f, [30 + i * 14, 48 + i * 14], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const check = interpolate(f, [40 + i * 14, 54 + i * 14], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          return (
            <div
              key={i}
              style={{
                height: 250,
                borderRadius: 20,
                border: `2px solid ${NEUTRAL.lineStrong}`,
                background: `linear-gradient(160deg, rgba(95,160,228,${0.22 * fillOp}), rgba(124,92,191,${0.22 * fillOp}))`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <div style={{ opacity: 1 - check }}>
                <CameraIcon />
              </div>
              <div style={{ position: "absolute", opacity: check, scale: String(0.6 + check * 0.4) }}>
                <svg width="70" height="70" viewBox="0 0 24 24" fill="none" stroke={COLORS.greenLight} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M8 12.5l2.5 2.5L16 9" />
                </svg>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const S5Evidence: React.FC = () => {
  return (
    <AbsoluteFill>
      <Backdrop tint="purple" />
      <AbsoluteFill style={{ padding: SAFE, flexDirection: "row", alignItems: "center", gap: 90 }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 16 }}>
            <Kicker color={COLORS.purpleLight}>Trazabilidad</Kicker>
          </div>
          <Title size={72} maxWidth={820}>
            Cada paso, con evidencia verificable
          </Title>
          <div style={{ display: "flex", flexDirection: "column", gap: 22, marginTop: 46 }}>
            <Item icon={<CameraIcon />} accent={COLORS.blueLight} title="4 fotos obligatorias" desc="Sin ellas, la etapa no avanza." start={26} />
            <Item icon={<SignIcon />} accent={COLORS.greenLight} title="Firma del responsable" desc="Firma digital en la entrega." start={36} />
            <Item icon={<CheckIcon />} accent={COLORS.purpleLight} title="Checklist de inspección" desc="Aprobado antes de continuar." start={46} />
          </div>
        </div>
        <Phone delay={16} />
      </AbsoluteFill>
    </AbsoluteFill>
  );
};
